import { signCloudinaryUpload } from 'api/signCloudinaryUpload';
import { action, makeObservable, observable, reaction, runInAction } from 'mobx';
import axios, { AxiosRequestConfig } from 'axios';
import { CloudinaryUploadResponse, moduleColors } from '@creative-kit/shared';
import { ulid } from 'ulid';
import type { RootStore } from '.';
import { db } from './data';
import { convertHeicToJpeg, isHeicFile } from './image/heicConversion';
import type { TrackLabel } from './analytics';
import { trackAction } from './analytics';

// --- Matching helpers (reused for suggestions) ---

function normalizeName(name: string): string {
  return name.replace(/[-_]/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
}

// exported for potential use in Feature 2 (drop-on-card suggestions)
export function normalizeFilename(filename: string): string {
  // Remove extension, then trailing _01, -v2, etc.
  const noExt = filename.replace(/\.[^.]+$/, '');
  const noSuffix = noExt.replace(/[-_]\d+$/, '');
  return normalizeName(noSuffix);
}

function folderMatchesCard(folderName: string, cardTitle: string): boolean {
  const nFolder = normalizeName(folderName);
  const nCard = normalizeName(cardTitle);
  // Minimum 3-char threshold to prevent false positives
  if (!nFolder || !nCard || nFolder.length < 3 || nCard.length < 3) return false;
  // Bidirectional contains — handles most real-world cases
  return nFolder.includes(nCard) || nCard.includes(nFolder);
}

interface FolderUploadTracker {
  sourceId: string;
  rowIds: string[];
  pending: number;
}

export interface UploadProgress {
  id: string;
  filename: string;
  progress: number;
  abort: () => void;
  retry?: () => void;
  error?: string;
  localPreviewUrl?: string; // Blob URL for instant preview before upload completes
}

export interface PendingConnect {
  sourceId: string;
  rowIds: string[];
  suggestedCardId?: string;
  suggestedModuleId?: string;
  folderName?: string;
}

export interface UploadImageOptions {
  skipConnectPrompt?: boolean; // When true, don't track for connect prompt
  /** Included in telemetry when the Cloudinary upload completes successfully */
  telemetryVia?: TrackLabel;
}

const MAX_UPLOAD_BYTES = 20 * 1000 * 1000; // Cloudinary Plus plan per-file limit
const CLOUDINARY_MAX_BYTES = 20 * 1000 * 1000; // Compress anything over 20 MB

// Debounce time for batching loose file uploads before showing connect prompt
const LOOSE_FILE_BATCH_DELAY_MS = 1500;

// Sentinel value for creating a new card from the connect prompt
export const NEW_CARD_ID = '__new_card__';

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas toBlob failed'))),
      type,
      quality,
    );
  });
}

async function compressImageIfNeeded(file: File): Promise<File> {
  if (file.size <= CLOUDINARY_MAX_BYTES) return file;

  const bitmap = await createImageBitmap(file);
  const { width, height } = bitmap;

  // Scale down if needed (cap longest edge at 4000px)
  const MAX_DIM = 4000;
  let targetW = width;
  let targetH = height;
  if (width > MAX_DIM || height > MAX_DIM) {
    const scale = MAX_DIM / Math.max(width, height);
    targetW = Math.round(width * scale);
    targetH = Math.round(height * scale);
  }

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(bitmap, 0, 0, targetW, targetH);
  bitmap.close();

  // Try quality levels until under limit
  for (const quality of [0.92, 0.85, 0.75, 0.6]) {
    const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    if (blob.size <= CLOUDINARY_MAX_BYTES) {
      return new File([blob], file.name, { type: 'image/jpeg' });
    }
  }

  // Last resort: scale to 2000px
  const smallScale = 2000 / Math.max(targetW, targetH);
  const smallCanvas = document.createElement('canvas');
  smallCanvas.width = Math.round(targetW * smallScale);
  smallCanvas.height = Math.round(targetH * smallScale);
  const smallCtx = smallCanvas.getContext('2d')!;
  smallCtx.drawImage(canvas, 0, 0, smallCanvas.width, smallCanvas.height);
  const blob = await canvasToBlob(smallCanvas, 'image/jpeg', 0.85);
  return new File([blob], file.name, { type: 'image/jpeg' });
}

export class UiImageSourceUploads {
  private rootStore: RootStore;
  sourceUploads: Record<string, UploadProgress[]> = {};
  showOnlyErrorRows: Record<string, boolean> = {};

  // Post-upload connect prompt state
  pendingConnect: PendingConnect | null = null;

  // Track in-flight uploads per folder for batch connect prompt
  private folderUploadTrackers: Map<string, FolderUploadTracker> = new Map();

  // Track loose (non-folder) file uploads per source with debounce
  private looseFileRowIds: Map<string, string[]> = new Map();
  private looseFileTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();

  // Concurrency limiter for uploads
  private uploadQueue: Array<() => Promise<void>> = [];
  private activeUploads = 0;
  private readonly MAX_CONCURRENT_UPLOADS = 5;

  constructor(rootStore: RootStore) {
    makeObservable<
      this,
      'addProgressToSource' | 'updateProgress' | 'removeProgress' | 'setProgressError'
    >(this, {
      sourceUploads: observable,
      showOnlyErrorRows: observable,
      pendingConnect: observable.ref,
      addProgressToSource: action,
      updateProgress: action,
      removeProgress: action,
      setProgressError: action,
      setShowOnlyErrorRows: action.bound,
      showConnectPrompt: action.bound,
      connectPending: action.bound,
      skipPending: action.bound,
    });

    this.rootStore = rootStore;

    // Clean up upload timers and pending state when session changes
    reaction(
      () => rootStore.sessionsStore.currentSessionId,
      () => {
        runInAction(() => {
          this.looseFileTimers.forEach((timer) => clearTimeout(timer));
          this.looseFileTimers.clear();
          this.looseFileRowIds.clear();
          this.folderUploadTrackers.clear();
          this.pendingConnect = null;
        });
      },
    );
  }

  private enqueueUpload(fn: () => Promise<void>) {
    this.uploadQueue.push(fn);
    this.processQueue();
  }

  private processQueue() {
    while (this.activeUploads < this.MAX_CONCURRENT_UPLOADS && this.uploadQueue.length > 0) {
      const next = this.uploadQueue.shift()!;
      this.activeUploads++;
      next().finally(() => {
        this.activeUploads--;
        this.processQueue();
      });
    }
  }

  private addProgressToSource(sourceId: string, progress: UploadProgress) {
    this.sourceUploads[sourceId] ||= [];
    this.sourceUploads[sourceId].push(progress);
  }

  private updateProgress(sourceId: string, itemId: string, newProgress: number) {
    const item = this.sourceUploads[sourceId]?.find((i) => i.id === itemId);
    if (item) {
      item.progress = newProgress;
    }
  }

  private removeProgress(sourceId: string, itemId: string) {
    const itemIdx = this.sourceUploads[sourceId]?.findIndex((i) => i.id === itemId);
    if (itemIdx >= 0) {
      // Clean up blob URL to prevent memory leak
      const item = this.sourceUploads[sourceId][itemIdx];
      if (item.localPreviewUrl) {
        URL.revokeObjectURL(item.localPreviewUrl);
      }
      this.sourceUploads[sourceId].splice(itemIdx, 1);
      // If no more errors exist, clear the filter
      if (!this.hasErrorRows(sourceId)) {
        this.setShowOnlyErrorRows(sourceId, false);
      }
    }
  }

  private setProgressError(sourceId: string, itemId: string, error: string) {
    const item = this.sourceUploads[sourceId]?.find((i) => i.id === itemId);
    if (item) {
      item.error = error;
    }
  }

  getProgress = (sourceId: string, rowId: string) =>
    this.sourceUploads[sourceId]?.find((p) => p.id === rowId);

  clearProgress = (sourceId: string, rowId: string) => {
    this.removeProgress(sourceId, rowId);
  };

  setShowOnlyErrorRows(sourceId: string, value: boolean) {
    this.showOnlyErrorRows[sourceId] = value;
  }

  hasErrorRows = (sourceId: string) => {
    return this.sourceUploads[sourceId]?.some((p) => p.error) || false;
  };

  getRowIdsWithErrors = (sourceId: string): Set<string> => {
    const rowIds = new Set<string>();
    this.sourceUploads[sourceId]?.forEach((p) => {
      if (p.error) {
        rowIds.add(p.id);
      }
    });
    return rowIds;
  };

  // --- Post-upload connect prompt ---

  showConnectPrompt(sourceId: string, rowIds: string[], folderName?: string) {
    if (!rowIds.length) return;

    const currentSession = this.rootStore?.sessionsStore?.currentSession;
    if (!currentSession) return;

    // Find modules that reference this source (for the module dropdown)
    let modules = currentSession.getModulesForSource(sourceId);
    if (!modules.length) {
      // Auto-create a default module for this image source
      const source = this.rootStore.sourcesStore.sources[sourceId];
      if (source) {
        currentSession.upsertModule({
          moduleId: ulid(),
          name: source.data.name || 'Images',
          sourceId,
          multi: true,
          table: true,
          color: moduleColors[currentSession.modules.length % moduleColors.length],
          fields: source.defaultFieldIds,
        });
        modules = currentSession.getModulesForSource(sourceId);
      }
      if (!modules.length) return;
    }

    // Try to suggest a card match
    let suggestedCardId: string | undefined;
    const cards = Object.values(this.rootStore.cardsStore.cards);

    if (folderName) {
      // Folder upload: match folder name against card titles
      const matching = cards.filter((card) => {
        const title = currentSession.computeMainLabel(card).mainLabel;
        return (
          folderMatchesCard(folderName, title || '') ||
          (card.data.manualTitle ? folderMatchesCard(folderName, card.data.manualTitle) : false)
        );
      });
      if (matching.length === 1) {
        suggestedCardId = matching[0].id;
      }
    }

    // Suggest the first multi-connection module for this source
    const multiModules = modules.filter((m) => m.multi);
    const suggestedModuleId = multiModules.length > 0 ? multiModules[0].id : modules[0].id;

    this.pendingConnect = {
      sourceId,
      rowIds,
      suggestedCardId,
      suggestedModuleId,
      folderName,
    };
  }

  async connectPending(cardId: string, moduleId: string) {
    const pending = this.pendingConnect;
    if (!pending) return;

    const currentSession = this.rootStore?.sessionsStore?.currentSession;
    if (!currentSession) return;

    try {
      if (cardId === NEW_CARD_ID) {
        await currentSession.addConnection({
          rowIds: pending.rowIds,
          moduleId,
        });
      } else {
        const card = this.rootStore.cardsStore.cards[cardId];
        if (!card) return;

        await currentSession.batchAddConnections({
          selectedCards: [card],
          rowIds: pending.rowIds,
          moduleId,
        });
      }
    } catch (err) {
      console.error('Connect failed:', err);
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'Failed to connect. Please try again.',
      });
      return; // Keep prompt open on failure so user can retry
    }

    runInAction(() => {
      this.pendingConnect = null;
    });
  }

  skipPending() {
    this.pendingConnect = null;
  }

  // Track a completed loose (non-folder) file upload.
  // Debounces so that a batch of dropped files shows one prompt.
  private trackLooseFileUpload(sourceId: string, rowId: string) {
    const existing = this.looseFileRowIds.get(sourceId) || [];
    existing.push(rowId);
    this.looseFileRowIds.set(sourceId, existing);

    // Reset the debounce timer
    const existingTimer = this.looseFileTimers.get(sourceId);
    if (existingTimer) clearTimeout(existingTimer);

    const timer = setTimeout(() => {
      this.looseFileTimers.delete(sourceId);
      const rowIds = this.looseFileRowIds.get(sourceId) || [];
      this.looseFileRowIds.delete(sourceId);
      if (rowIds.length > 0) {
        this.showConnectPrompt(sourceId, rowIds);
      }
    }, LOOSE_FILE_BATCH_DELAY_MS);
    this.looseFileTimers.set(sourceId, timer);
  }

  /**
   * If the file is HEIC/HEIF, convert to JPEG and re-check against the 20MB
   * limit (HEIC decodes can balloon). Sets `setProgressError` on the caller's
   * behalf when the converted file is oversize; the caller is still responsible
   * for any method-specific error persistence (Firestore stub, tracker cleanup)
   * because that behavior differs across upload surfaces.
   *
   * Non-HEIC files pass through untouched — the synchronous size check at each
   * call site handles them before we get here.
   */
  private async normalizeForUpload(
    imageFile: File,
    sourceId: string,
    progressKey: string,
  ): Promise<{ kind: 'ready'; file: File } | { kind: 'oversize'; file: File }> {
    if (!isHeicFile(imageFile)) return { kind: 'ready', file: imageFile };
    const converted = await convertHeicToJpeg(imageFile);
    if (converted.size > MAX_UPLOAD_BYTES) {
      this.setProgressError(
        sourceId,
        progressKey,
        'This file exceeds 20MB. Try exporting as JPEG first.',
      );
      return { kind: 'oversize', file: converted };
    }
    return { kind: 'ready', file: converted };
  }

  uploadImageToSource = (
    sourceId: string,
    imageFile: File,
    retryCallback?: () => void,
    options?: UploadImageOptions,
  ) => {
    // Skip non-image files (directory entries, .DS_Store, etc.)
    // HEIC/HEIF files are accepted even though Chrome/Firefox often report an
    // empty File.type for them — we detect by extension and convert to JPEG
    // inside the async block below.
    if (!isHeicFile(imageFile) && !imageFile.type.startsWith('image/')) {
      return '';
    }

    const telemetryVia = options?.telemetryVia;

    if (!this.rootStore?.sourcesStore?.sources) {
      console.error('uploadImageToSource: sourcesStore not initialized');
      return '';
    }

    const source = this.rootStore.sourcesStore.sources[sourceId];
    if (!source) {
      console.error('uploadImageToSource: source not found', sourceId);
      return '';
    }

    const { sessionId } = source;

    const controller = new AbortController();
    const rowId = source.addEmptyImageRow();
    const localPreviewUrl = URL.createObjectURL(imageFile);
    const progressItem: UploadProgress = {
      id: rowId,
      filename: imageFile.name,
      progress: 0,
      localPreviewUrl,
      abort: () => {
        controller.abort();
        this.removeProgress(sourceId, rowId);
        if (source.rows[rowId]) {
          source.deleteRow(rowId);
        }
      },
      retry: retryCallback,
    };
    this.addProgressToSource(sourceId, progressItem);

    // Extract folder name early for tracker registration
    const folderName = imageFile.webkitRelativePath
      ? imageFile.webkitRelativePath.split('/')[0]
      : '';

    // Register with folder upload tracker for batch connect prompt
    if (folderName) {
      const key = `${sourceId}::${folderName}`;
      const tracker = this.folderUploadTrackers.get(key) || {
        sourceId,
        rowIds: [],
        pending: 0,
      };
      tracker.rowIds.push(rowId);
      tracker.pending++;
      this.folderUploadTrackers.set(key, tracker);
    }

    // Synchronous size check for non-HEIC files. HEIC size must be checked
    // AFTER decode-to-JPEG because the HEIC compression ratio is unrelated to
    // what we'll actually upload.
    if (!isHeicFile(imageFile) && imageFile.size > MAX_UPLOAD_BYTES) {
      this.setProgressError(
        sourceId,
        rowId,
        'This file exceeds 20MB. Try exporting as JPEG first.',
      );
      // Save the filename directly to Firestore so user can see which file failed
      const rowRef = db.sessionSourceRows(sessionId, sourceId).doc(rowId);
      rowRef.set(
        {
          image: {
            originalFilename: imageFile.name,
            width: 0,
            height: 0,
            bytes: imageFile.size,
            storage: {
              type: 'cloudinary',
              cloudName: '',
              publicId: '',
            },
          },
        },
        { merge: true },
      );
      // Show only error rows to make it easy to see what needs to be fixed
      this.setShowOnlyErrorRows(sourceId, true);

      // Decrement tracker for oversized files (they won't complete normally)
      if (!options?.skipConnectPrompt && folderName) {
        const key = `${sourceId}::${folderName}`;
        const tracker = this.folderUploadTrackers.get(key);
        if (tracker) {
          const idx = tracker.rowIds.indexOf(rowId);
          if (idx >= 0) tracker.rowIds.splice(idx, 1);
          tracker.pending--;
          if (tracker.pending === 0) {
            this.folderUploadTrackers.delete(key);
            this.showConnectPrompt(sourceId, tracker.rowIds, folderName);
          }
        }
      }

      // Don't delete row immediately - let user retry or abort
      return rowId;
    }

    this.enqueueUpload(async () => {
      try {
        // HEIC → JPEG so the rest of the pipeline works universally.
        const normalized = await this.normalizeForUpload(imageFile, sourceId, rowId);
        if (normalized.kind === 'oversize') {
          const rowRef = db.sessionSourceRows(sessionId, sourceId).doc(rowId);
          rowRef.set(
            {
              image: {
                originalFilename: normalized.file.name,
                width: 0,
                height: 0,
                bytes: normalized.file.size,
                storage: {
                  type: 'cloudinary',
                  cloudName: '',
                  publicId: '',
                },
              },
            },
            { merge: true },
          );
          this.setShowOnlyErrorRows(sourceId, true);
          if (!options?.skipConnectPrompt && folderName) {
            const key = `${sourceId}::${folderName}`;
            const tracker = this.folderUploadTrackers.get(key);
            if (tracker) {
              const idx = tracker.rowIds.indexOf(rowId);
              if (idx >= 0) tracker.rowIds.splice(idx, 1);
              tracker.pending--;
              if (tracker.pending === 0) {
                this.folderUploadTrackers.delete(key);
                this.showConnectPrompt(sourceId, tracker.rowIds, folderName);
              }
            }
          }
          return;
        }
        const uploadFileSource = normalized.file;

        const {
          data: { apiKey, cloudName, folder, signature, timestamp },
        } = await signCloudinaryUpload({
          sessionId,
          sourceId,
        });

        const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`;

        const uploadFile = await compressImageIfNeeded(uploadFileSource);
        const fd = new FormData();
        fd.append('file', uploadFile);
        fd.append('api_key', apiKey);
        fd.append('folder', folder);
        fd.append('signature', signature);
        fd.append('timestamp', timestamp);

        const uploadConfig: AxiosRequestConfig = {
          onUploadProgress: (event) => {
            const progress = event.total ? event.loaded / event.total : 0;
            this.updateProgress(sourceId, progressItem.id, progress);
          },
          signal: controller.signal,
        };

        const uploadResponse = await axios.post<CloudinaryUploadResponse>(
          uploadUrl,
          fd,
          uploadConfig,
        );
        const { bytes, height, width, public_id: publicId } = uploadResponse.data;
        source.rows[rowId].setImage(
          {
            bytes,
            height,
            width,
            originalFilename: uploadFileSource.name,
            storage: {
              type: 'cloudinary',
              cloudName,
              publicId,
            },
          },
          folderName || undefined,
        );
        this.removeProgress(sourceId, progressItem.id);
        trackAction('image_uploaded', telemetryVia);
        if (!options?.skipConnectPrompt) {
          if (folderName) {
            const key = `${sourceId}::${folderName}`;
            const tracker = this.folderUploadTrackers.get(key);
            if (tracker) {
              tracker.pending--;
              if (tracker.pending === 0) {
                this.folderUploadTrackers.delete(key);
                this.showConnectPrompt(sourceId, tracker.rowIds, folderName);
              }
            }
          } else {
            // Loose file — batch with debounce, then show prompt
            this.trackLooseFileUpload(sourceId, rowId);
          }
        }
      } catch (err) {
        if (axios.isAxiosError(err)) {
          this.setProgressError(
            sourceId,
            progressItem.id,
            err.response?.data.error?.message || 'An unknown error occurred while uploading',
          );
        } else if (err && typeof err === 'object' && 'message' in err) {
          this.setProgressError(
            sourceId,
            progressItem.id,
            (err as { message: string }).message || 'An unknown error occurred while uploading',
          );
        }
        // Clean up blob URL to prevent memory leak
        if (progressItem.localPreviewUrl) {
          URL.revokeObjectURL(progressItem.localPreviewUrl);
        }

        // Remove failed rowId from tracker before checking batch completion
        if (!options?.skipConnectPrompt && folderName) {
          const key = `${sourceId}::${folderName}`;
          const tracker = this.folderUploadTrackers.get(key);
          if (tracker) {
            const idx = tracker.rowIds.indexOf(rowId);
            if (idx >= 0) tracker.rowIds.splice(idx, 1);
            tracker.pending--;
            if (tracker.pending === 0) {
              this.folderUploadTrackers.delete(key);
              this.showConnectPrompt(sourceId, tracker.rowIds, folderName);
            }
          }
        }

        source.deleteRow(rowId);
      }
    });

    return rowId;
  };

  uploadImageToRow = (
    sourceId: string,
    rowId: string,
    imageFile: File,
    retryCallback?: () => void,
    telemetryVia?: TrackLabel,
  ) => {
    // Skip non-image files (directory entries, .DS_Store, etc.).
    // HEIC/HEIF is accepted — see uploadImageToSource for rationale.
    if (!isHeicFile(imageFile) && !imageFile.type.startsWith('image/')) {
      return;
    }

    if (!this.rootStore?.sourcesStore?.sources) {
      console.error('uploadImageToRow: sourcesStore not initialized');
      return;
    }

    const source = this.rootStore.sourcesStore.sources[sourceId];
    if (!source) {
      console.error('uploadImageToRow: source not found', sourceId);
      return;
    }

    const { sessionId } = source;

    const controller = new AbortController();
    const localPreviewUrl = URL.createObjectURL(imageFile);
    const progressItem: UploadProgress = {
      id: rowId,
      filename: imageFile.name,
      progress: 0,
      localPreviewUrl,
      abort: () => {
        controller.abort();
        this.removeProgress(sourceId, rowId);
      },
      retry: retryCallback,
    };
    this.addProgressToSource(sourceId, progressItem);

    // HEIC size is validated after conversion inside the async block below.
    if (!isHeicFile(imageFile) && imageFile.size > MAX_UPLOAD_BYTES) {
      this.setProgressError(
        sourceId,
        rowId,
        'This file exceeds 20MB. Try exporting as JPEG first.',
      );
      // Save the filename so user can see which file failed
      if (source.rows[rowId]) {
        source.rows[rowId].setImage({
          originalFilename: imageFile.name,
          width: 0,
          height: 0,
          bytes: imageFile.size,
          storage: {
            type: 'cloudinary',
            cloudName: '',
            publicId: '',
          },
        });
      } else {
        // Row doesn't exist in local state yet, write directly to Firestore
        const rowRef = db.sessionSourceRows(sessionId, sourceId).doc(rowId);
        rowRef.set(
          {
            image: {
              originalFilename: imageFile.name,
              width: 0,
              height: 0,
              bytes: imageFile.size,
              storage: {
                type: 'cloudinary',
                cloudName: '',
                publicId: '',
              },
            },
          },
          { merge: true },
        );
      }
      // Show only error rows to make it easy to see what needs to be fixed
      this.setShowOnlyErrorRows(sourceId, true);
      return;
    }

    (async () => {
      try {
        const normalized = await this.normalizeForUpload(imageFile, sourceId, rowId);
        if (normalized.kind === 'oversize') {
          const errorImage = {
            originalFilename: normalized.file.name,
            width: 0,
            height: 0,
            bytes: normalized.file.size,
            storage: { type: 'cloudinary' as const, cloudName: '', publicId: '' },
          };
          if (source.rows[rowId]) {
            source.rows[rowId].setImage(errorImage);
          } else {
            const rowRef = db.sessionSourceRows(sessionId, sourceId).doc(rowId);
            rowRef.set({ image: errorImage }, { merge: true });
          }
          this.setShowOnlyErrorRows(sourceId, true);
          return;
        }
        const uploadFileSource = normalized.file;

        const {
          data: { apiKey, cloudName, folder, signature, timestamp },
        } = await signCloudinaryUpload({
          sessionId,
          sourceId,
        });

        const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`;

        const uploadFile = await compressImageIfNeeded(uploadFileSource);
        const fd = new FormData();
        fd.append('file', uploadFile);
        fd.append('api_key', apiKey);
        fd.append('folder', folder);
        fd.append('signature', signature);
        fd.append('timestamp', timestamp);

        const uploadConfig: AxiosRequestConfig = {
          onUploadProgress: (event) => {
            if (event.total) {
              this.updateProgress(sourceId, progressItem.id, event.loaded / event.total);
            }
          },
          signal: controller.signal,
        };

        const uploadResponse = await axios.post<CloudinaryUploadResponse>(
          uploadUrl,
          fd,
          uploadConfig,
        );
        const { bytes, height, width, public_id: publicId } = uploadResponse.data;
        // Extract folder name from webkitRelativePath
        const folderName = imageFile.webkitRelativePath
          ? imageFile.webkitRelativePath.split('/')[0]
          : '';
        source.rows[rowId].setImage(
          {
            bytes,
            height,
            width,
            originalFilename: uploadFileSource.name,
            storage: {
              type: 'cloudinary',
              cloudName,
              publicId,
            },
          },
          folderName || undefined,
        );
        this.removeProgress(sourceId, progressItem.id);
        trackAction('image_uploaded', telemetryVia);
      } catch (err) {
        if (axios.isAxiosError(err)) {
          this.setProgressError(
            sourceId,
            progressItem.id,
            err.response?.data.error?.message || 'An unknown error occurred while uploading',
          );
        } else if (err && typeof err === 'object' && 'message' in err) {
          this.setProgressError(
            sourceId,
            progressItem.id,
            (err as { message: string }).message || 'An unknown error occurred while uploading',
          );
        }
        // Clean up blob URL to prevent memory leak
        if (progressItem.localPreviewUrl) {
          URL.revokeObjectURL(progressItem.localPreviewUrl);
        }
      }
    })();
  };

  // Per-field-column image upload (for image column type)
  getFieldColumnProgress = (sourceId: string, rowId: string, fieldId: string) =>
    this.sourceUploads[sourceId]?.find((p) => p.id === `${rowId}:${fieldId}`);

  clearFieldColumnProgress = (sourceId: string, rowId: string, fieldId: string) => {
    this.removeProgress(sourceId, `${rowId}:${fieldId}`);
  };

  uploadImageToFieldColumn = (
    sourceId: string,
    rowId: string,
    fieldId: string,
    imageFile: File,
    retryCallback?: () => void,
  ) => {
    // Skip non-image files. HEIC/HEIF is accepted and converted below.
    if (!isHeicFile(imageFile) && !imageFile.type.startsWith('image/')) {
      return;
    }

    if (!this.rootStore?.sourcesStore?.sources) {
      console.error('uploadImageToFieldColumn: sourcesStore not initialized');
      return;
    }

    const source = this.rootStore.sourcesStore.sources[sourceId];
    if (!source) {
      console.error('uploadImageToFieldColumn: source not found', sourceId);
      return;
    }

    const { sessionId } = source;
    const progressKey = `${rowId}:${fieldId}`;

    const controller = new AbortController();
    const localPreviewUrl = URL.createObjectURL(imageFile);
    const progressItem: UploadProgress = {
      id: progressKey,
      filename: imageFile.name,
      progress: 0,
      localPreviewUrl,
      abort: () => {
        controller.abort();
        this.removeProgress(sourceId, progressKey);
      },
      retry: retryCallback,
    };
    this.addProgressToSource(sourceId, progressItem);

    // HEIC size is validated after conversion inside the async block below.
    if (!isHeicFile(imageFile) && imageFile.size > MAX_UPLOAD_BYTES) {
      this.setProgressError(
        sourceId,
        progressKey,
        'This file exceeds 20MB. Try exporting as JPEG first.',
      );
      return;
    }

    (async () => {
      try {
        const normalized = await this.normalizeForUpload(imageFile, sourceId, progressKey);
        if (normalized.kind === 'oversize') return;
        const uploadFileSource = normalized.file;

        const {
          data: { apiKey, cloudName, folder, signature, timestamp },
        } = await signCloudinaryUpload({
          sessionId,
          sourceId,
        });

        const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`;

        const fd = new FormData();
        fd.append('file', uploadFileSource);
        fd.append('api_key', apiKey);
        fd.append('folder', folder);
        fd.append('signature', signature);
        fd.append('timestamp', timestamp);

        const uploadConfig: AxiosRequestConfig = {
          onUploadProgress: (event) => {
            if (event.total) {
              this.updateProgress(sourceId, progressKey, event.loaded / event.total);
            }
          },
          signal: controller.signal,
        };

        const uploadResponse = await axios.post<CloudinaryUploadResponse>(
          uploadUrl,
          fd,
          uploadConfig,
        );
        const { bytes, height, width, public_id: publicId } = uploadResponse.data;
        source.rows[rowId].setFieldImage(fieldId, {
          bytes,
          height,
          width,
          originalFilename: uploadFileSource.name,
          storage: {
            type: 'cloudinary',
            cloudName,
            publicId,
          },
        });
        this.removeProgress(sourceId, progressKey);
      } catch (err) {
        if (axios.isAxiosError(err)) {
          this.setProgressError(
            sourceId,
            progressKey,
            err.response?.data.error?.message || 'An unknown error occurred while uploading',
          );
        } else if (err && typeof err === 'object' && 'message' in err) {
          this.setProgressError(
            sourceId,
            progressKey,
            (err as { message: string }).message || 'An unknown error occurred while uploading',
          );
        }
        // Clean up blob URL to prevent memory leak
        if (progressItem.localPreviewUrl) {
          URL.revokeObjectURL(progressItem.localPreviewUrl);
        }
      }
    })();
  };

  uploadAttachmentToRow = (
    sourceId: string,
    rowId: string,
    imageFile: File,
    retryCallback?: () => void,
  ) => {
    // Skip non-image files. HEIC/HEIF is accepted and converted below.
    if (!isHeicFile(imageFile) && !imageFile.type.startsWith('image/')) {
      return;
    }

    if (!this.rootStore?.sourcesStore?.sources) {
      console.error('uploadAttachmentToRow: sourcesStore not initialized');
      return;
    }

    const source = this.rootStore.sourcesStore.sources[sourceId];
    if (!source) {
      console.error('uploadAttachmentToRow: source not found', sourceId);
      return;
    }

    const { sessionId } = source;

    const controller = new AbortController();
    const localPreviewUrl = URL.createObjectURL(imageFile);
    const progressItem: UploadProgress = {
      id: rowId,
      filename: imageFile.name,
      progress: 0,
      localPreviewUrl,
      abort: () => {
        controller.abort();
        this.removeProgress(sourceId, rowId);
      },
      retry: retryCallback,
    };
    this.addProgressToSource(sourceId, progressItem);

    // HEIC size is validated after conversion inside the async block below.
    if (!isHeicFile(imageFile) && imageFile.size > MAX_UPLOAD_BYTES) {
      this.setProgressError(
        sourceId,
        rowId,
        'This file exceeds 20MB. Try exporting as JPEG first.',
      );
      // Save the filename so user can see which file failed
      if (source.rows[rowId]) {
        source.rows[rowId].setAttachment({
          originalFilename: imageFile.name,
          width: 0,
          height: 0,
          bytes: imageFile.size,
          storage: {
            type: 'cloudinary',
            cloudName: '',
            publicId: '',
          },
        });
      } else {
        // Row doesn't exist in local state yet, write directly to Firestore
        const rowRef = db.sessionSourceRows(sessionId, sourceId).doc(rowId);
        rowRef.set(
          {
            attachment: {
              originalFilename: imageFile.name,
              width: 0,
              height: 0,
              bytes: imageFile.size,
              storage: {
                type: 'cloudinary',
                cloudName: '',
                publicId: '',
              },
            },
          },
          { merge: true },
        );
      }
      // Show only error rows to make it easy to see what needs to be fixed
      this.setShowOnlyErrorRows(sourceId, true);
      return;
    }

    (async () => {
      try {
        const normalized = await this.normalizeForUpload(imageFile, sourceId, rowId);
        if (normalized.kind === 'oversize') {
          const errorAttachment = {
            originalFilename: normalized.file.name,
            width: 0,
            height: 0,
            bytes: normalized.file.size,
            storage: { type: 'cloudinary' as const, cloudName: '', publicId: '' },
          };
          if (source.rows[rowId]) {
            source.rows[rowId].setAttachment(errorAttachment);
          } else {
            const rowRef = db.sessionSourceRows(sessionId, sourceId).doc(rowId);
            rowRef.set({ attachment: errorAttachment }, { merge: true });
          }
          this.setShowOnlyErrorRows(sourceId, true);
          return;
        }
        const uploadFileSource = normalized.file;

        const {
          data: { apiKey, cloudName, folder, signature, timestamp },
        } = await signCloudinaryUpload({
          sessionId,
          sourceId,
        });

        const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`;

        const uploadFile = await compressImageIfNeeded(uploadFileSource);
        const fd = new FormData();
        fd.append('file', uploadFile);
        fd.append('api_key', apiKey);
        fd.append('folder', folder);
        fd.append('signature', signature);
        fd.append('timestamp', timestamp);

        const uploadConfig: AxiosRequestConfig = {
          onUploadProgress: (event) => {
            const progress = event.total ? event.loaded / event.total : 0;
            this.updateProgress(sourceId, progressItem.id, progress);
          },
          signal: controller.signal,
        };

        const uploadResponse = await axios.post<CloudinaryUploadResponse>(
          uploadUrl,
          fd,
          uploadConfig,
        );
        const { bytes, height, width, public_id: publicId } = uploadResponse.data;
        source.rows[rowId].setAttachment({
          bytes,
          height,
          width,
          originalFilename: uploadFileSource.name,
          storage: {
            type: 'cloudinary',
            cloudName,
            publicId,
          },
        });
        this.removeProgress(sourceId, progressItem.id);
      } catch (err) {
        if (axios.isAxiosError(err)) {
          this.setProgressError(
            sourceId,
            progressItem.id,
            err.response?.data.error?.message || 'An unknown error occurred while uploading',
          );
        } else if (err && typeof err === 'object' && 'message' in err) {
          this.setProgressError(
            sourceId,
            progressItem.id,
            (err as { message: string }).message || 'An unknown error occurred while uploading',
          );
        }
        // Clean up blob URL to prevent memory leak
        if (progressItem.localPreviewUrl) {
          URL.revokeObjectURL(progressItem.localPreviewUrl);
        }
      }
    })();
  };
}
